import React, { useState } from "react";
import { format } from "date-fns";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Eye, FileSpreadsheet, Filter } from "lucide-react";
import { toast } from "sonner";

async function downloadBlob(url, filename) {
  const r = await fetch(`${API_BASE}${url}`, { credentials: "include" });
  if (!r.ok) {
    let msg = `Download failed: ${r.status}`;
    try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch (err) { void err; }
    throw new Error(msg);
  }
  const cd = r.headers.get("content-disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const fname = m ? m[1] : filename;
  const blob = await r.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fname;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export default function DatasetsTable({ datasets, emptyMessage = "No datasets to show", isPanIndia = false, fState = "", fCity = "" }) {
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState("");

  const withFilter = (url) => {
    const p = new URLSearchParams();
    if (isPanIndia && fState) p.set("state", fState);
    if (isPanIndia && fCity) p.set("city", fCity);
    const qs = p.toString();
    return qs ? `${url}${url.includes("?") ? "&" : "?"}${qs}` : url;
  };

  const openPreview = async (fileId) => {
    try {
      const params = {};
      if (isPanIndia && fState) params.state = fState;
      if (isPanIndia && fCity) params.city = fCity;
      const { data } = await api.get(`/customer/dataset/${fileId}/preview`, { params });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const dl = async (url, name, key) => {
    setDownloading(key);
    try { await downloadBlob(url, name); toast.success("Download started"); }
    catch (e) { toast.error(e.message); }
    finally { setDownloading(""); }
  };

  if (!datasets || datasets.length === 0) {
    return (
      <Card className="p-10 text-center">
        <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground" strokeWidth={1.25} />
        <div className="mt-4 font-heading font-semibold">{emptyMessage}</div>
        <p className="mt-1.5 text-sm text-muted-foreground">Files uploaded by the admin under your package will appear here.</p>
      </Card>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Filename</th>
                <th className="text-right px-4 py-3">Records</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {datasets.map((d) => (
                <tr key={d.id} className="border-t border-border hover:bg-accent/40 transition-colors" data-testid={`dataset-row-${d.id}`}>
                  <td className="px-4 py-3 num">{format(new Date(d.upload_date), "dd MMM yyyy · HH:mm")}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${d.type === "gst" ? "bg-primary/10 text-primary" : "bg-emerald-500/10 text-emerald-500"}`}>
                      {d.type.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{d.display_name || d.filename}</td>
                  <td className="px-4 py-3 text-right num font-medium">{(d.visible_count || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex flex-wrap gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => openPreview(d.id)} data-testid={`preview-${d.id}`}>
                        <Eye className="h-4 w-4 mr-1.5" /> Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => dl(`/customer/download/file/${d.id}`, `${d.type}_${d.id}.csv`, `full-${d.id}`)}
                        disabled={downloading === `full-${d.id}`}
                        data-testid={`download-full-${d.id}`}
                      >
                        <Download className="h-4 w-4 mr-1.5" /> {downloading === `full-${d.id}` ? "..." : "Download"}
                      </Button>
                      {isPanIndia && (fState || fCity) && (
                        <Button
                          size="sm"
                          onClick={() => dl(withFilter(`/customer/download/file/${d.id}`), `${d.type}_filtered.csv`, `flt-${d.id}`)}
                          disabled={downloading === `flt-${d.id}`}
                          data-testid={`download-filtered-${d.id}`}
                        >
                          <Filter className="h-4 w-4 mr-1.5" /> Filtered
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-[95vw] w-full sm:max-w-4xl md:max-w-5xl lg:max-w-6xl bg-popover" data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle className="truncate">Preview · {preview?.file?.display_name || preview?.file?.filename}</DialogTitle>
            <DialogDescription>
              First 10 rows visible under your subscription filters. Scroll horizontally if the table is wider than the modal.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div>
              <div className="text-xs text-muted-foreground mb-3 num">
                Uploaded {new Date(preview.file.upload_date).toLocaleString()} · {preview.rows.length} sample row(s)
              </div>
              <div className="overflow-auto max-h-[55vh] border border-border rounded-md">
                <table className="min-w-full text-xs">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      {(preview.columns || Object.keys(preview.rows[0] || {})).map((k) => (
                        <th key={k} className="text-left px-3 py-2 font-medium whitespace-nowrap">{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, i) => (
                      <tr key={row.id || row.GSTIN || row.CIN || `row-${i}`} className="border-t border-border">
                        {(preview.columns || Object.keys(row)).map((k) => (
                          <td key={k} className="px-3 py-2 whitespace-nowrap text-muted-foreground max-w-[300px] overflow-hidden text-ellipsis" title={String(row[k] ?? "")}>{String(row[k] ?? "")}</td>
                        ))}
                      </tr>
                    ))}
                    {preview.rows.length === 0 && (
                      <tr><td className="px-3 py-6 text-center text-muted-foreground">No rows in your filter for this file</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => dl(`/customer/download/file/${preview.file.id}`, "download.csv", `pv-full-${preview.file.id}`)} data-testid="preview-download-full">
                  <Download className="h-4 w-4 mr-1.5" /> Download full
                </Button>
                {isPanIndia && (fState || fCity) && (
                  <Button onClick={() => dl(withFilter(`/customer/download/file/${preview.file.id}`), "filtered.csv", `pv-flt-${preview.file.id}`)} data-testid="preview-download-filtered">
                    <Filter className="h-4 w-4 mr-1.5" /> Download filtered
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
